// Persisted display preference: whether the rune board shows the (semi-mnemonic)
// shorthand codes or opaque placeholder symbols with no hint of meaning. Symbols is
// the real player experience -- per design/01_Vision.md, a player is never told a
// rune's name, only its glyph. Shorthand is a developer-only debugging convenience
// (Developer Tools submenu), not a player-facing choice, hence defaulting off.
export type RuneDisplayMode = 'shorthand' | 'symbols';

const STORAGE_KEY = 'rune-world-game-display-mode';

function loadDisplayMode(): RuneDisplayMode {
  return localStorage.getItem(STORAGE_KEY) === 'shorthand' ? 'shorthand' : 'symbols';
}

let displayMode: RuneDisplayMode = loadDisplayMode();

export function getDisplayMode(): RuneDisplayMode {
  return displayMode;
}

export function setDisplayMode(mode: RuneDisplayMode): void {
  displayMode = mode;
  localStorage.setItem(STORAGE_KEY, mode);
}

// Persisted preference for how a cast's result is worded: "narrative" is a short
// in-world sentence of what happened -- e.g. "The bookshelf smoulders. You feel your
// body temperature drop." -- and is the real player experience; "debug" is the raw
// resolve() output (dimensional signature, cost, warnings), a developer-only
// debugging convenience (Developer Tools submenu), hence defaulting off.
export type CastTextMode = 'debug' | 'narrative';

const CAST_TEXT_STORAGE_KEY = 'rune-world-game-cast-text-mode';

function loadCastTextMode(): CastTextMode {
  return localStorage.getItem(CAST_TEXT_STORAGE_KEY) === 'debug' ? 'debug' : 'narrative';
}

let castTextMode: CastTextMode = loadCastTextMode();

export function getCastTextMode(): CastTextMode {
  return castTextMode;
}

export function setCastTextMode(mode: CastTextMode): void {
  castTextMode = mode;
  localStorage.setItem(CAST_TEXT_STORAGE_KEY, mode);
}

// Persisted preference for which placeholder sprite the player character uses.
// Purely cosmetic -- GameScene generates a texture set per id and listens for
// changes so picking a new one from the Menu is reflected immediately, not just on
// next reload.
export type SpriteId = 'default' | 'witch' | 'wizard';

const SPRITE_STORAGE_KEY = 'rune-world-game-sprite-id';

function loadSpriteId(): SpriteId {
  const stored = localStorage.getItem(SPRITE_STORAGE_KEY);
  return stored === 'witch' || stored === 'wizard' ? stored : 'default';
}

let spriteId: SpriteId = loadSpriteId();
const spriteIdListeners: Array<(id: SpriteId) => void> = [];

export function getSpriteId(): SpriteId {
  return spriteId;
}

export function setSpriteId(id: SpriteId): void {
  spriteId = id;
  localStorage.setItem(SPRITE_STORAGE_KEY, id);
  spriteIdListeners.forEach((listener) => listener(id));
}

export function onSpriteIdChange(listener: (id: SpriteId) => void): void {
  spriteIdListeners.push(listener);
}

// Persisted preference for whether the rune board shows every rune (Developer
// Mode) or only ones learned via in-world books (learnedRunes.ts). Defaults to
// Developer Mode off -- restricted/discovery mode is the real player experience;
// Developer Mode is an opt-in debugging aid (Developer Tools submenu), not the
// starting state.
const DEV_MODE_STORAGE_KEY = 'rune-world-game-dev-mode';

function loadDevMode(): boolean {
  return localStorage.getItem(DEV_MODE_STORAGE_KEY) === 'true';
}

let devMode: boolean = loadDevMode();

export function getDevMode(): boolean {
  return devMode;
}

export function setDevMode(value: boolean): void {
  devMode = value;
  localStorage.setItem(DEV_MODE_STORAGE_KEY, String(value));
}

// Persisted preference for how the player moves on touch devices: tapping a
// screen quadrant relative to center (the original scheme, invisible until
// touched) or a fixed on-screen D-pad. Keyboard/WASD always work regardless of
// this setting -- it only changes which touch scheme is active, so it can't
// break any test or desktop flow that drives movement via the keyboard.
export type MovementInputMode = 'quadrant' | 'dpad';

const MOVEMENT_INPUT_MODE_KEY = 'rune-world-game-movement-input-mode';

function loadMovementInputMode(): MovementInputMode {
  return localStorage.getItem(MOVEMENT_INPUT_MODE_KEY) === 'dpad' ? 'dpad' : 'quadrant';
}

let movementInputMode: MovementInputMode = loadMovementInputMode();

export function getMovementInputMode(): MovementInputMode {
  return movementInputMode;
}

export function setMovementInputMode(mode: MovementInputMode): void {
  movementInputMode = mode;
  localStorage.setItem(MOVEMENT_INPUT_MODE_KEY, mode);
}

// Persisted preference for how many screen pixels one tile is drawn at.
//
// The world is authored, simulated and reasoned about in 32px tiles
// (GameScene's TILE) and that never changes -- this is purely how close the
// camera stands, applied as a zoom of `tileSize / 32`. So a larger tile means
// a bigger, closer view of *fewer* tiles, and nothing about the room, the
// simulation or what counts as "adjacent" moves with it.
//
// 32 stays the default because it is what every room was authored and framed
// against; the two larger sizes are for phones (where 32px tiles are small
// enough to be hard to read and hard to tap) and the smaller one for taking in
// a large room like room16 at a glance.
export type TileSize = 24 | 32 | 48 | 64;

// Not exported: the menu spells its four options out in markup like every other
// settings group does, and this exists to reject anything else that turns up in
// storage -- a hand-edited key, or a size that a later build stopped offering.
const TILE_SIZES: readonly TileSize[] = [24, 32, 48, 64];

const TILE_SIZE_KEY = 'rune-world-game-tile-size';

function loadTileSize(): TileSize {
  const stored = Number(localStorage.getItem(TILE_SIZE_KEY));
  return (TILE_SIZES as readonly number[]).includes(stored) ? (stored as TileSize) : 32;
}

let tileSize: TileSize = loadTileSize();
const tileSizeListeners: Array<(size: TileSize) => void> = [];

export function getTileSize(): TileSize {
  return tileSize;
}

export function setTileSize(size: TileSize): void {
  tileSize = size;
  localStorage.setItem(TILE_SIZE_KEY, String(size));
  tileSizeListeners.forEach((listener) => listener(size));
}

// Same shape as onSpriteIdChange above, and for the same reason: the scene has
// to re-zoom the camera (and re-centre a room that now over- or under-fills the
// screen) the moment this changes, not on the next room entry.
export function onTileSizeChange(listener: (size: TileSize) => void): void {
  tileSizeListeners.push(listener);
}

// Persisted preference for what makes time pass: the wall clock the game
// shipped on, where the world decays and regenerates while you stand and think,
// or a turn clock, where it advances by one fixed quantum per action you take
// and not otherwise.
//
// A player-facing option, not a Developer Tools one -- which is why it lives up
// here beside the movement-input mode rather than down among the debug overlays.
// The two are the same shape deliberately (a persisted string plus a listener),
// because this is the second setting that has to reach something already
// running: the scene has to stop driving the world from its frame loop, and the
// HUD has to grow an Undo button, the moment it changes.
//
// **Undo rides on this rather than being its own switch.** The two are
// separable in principle and useless apart in practice: restoring a snapshot
// into a world whose wall clock has kept running gives the player back a
// position the world has already moved past, which is worse than no undo at
// all. So turn mode means undo, and the sim never learns that undo exists --
// `clock.msPerTurn` is a physics value the simulation reads, and the history
// stack is entirely the client's (see undoHistory.ts).
export type ClockMode = 'wall' | 'turn';

const CLOCK_MODE_KEY = 'rune-world-game-clock-mode';

function loadClockMode(): ClockMode {
  return localStorage.getItem(CLOCK_MODE_KEY) === 'turn' ? 'turn' : 'wall';
}

let clockMode: ClockMode = loadClockMode();
const clockModeListeners: Array<(mode: ClockMode) => void> = [];

export function getClockMode(): ClockMode {
  return clockMode;
}

export function setClockMode(mode: ClockMode): void {
  clockMode = mode;
  localStorage.setItem(CLOCK_MODE_KEY, mode);
  clockModeListeners.forEach((listener) => listener(mode));
}

export function onClockModeChange(listener: (mode: ClockMode) => void): void {
  clockModeListeners.push(listener);
}

// Developer Tools option: show each rune's real description (the "answer key",
// normally never surfaced to players even in shorthand mode) as its tooltip,
// regardless of display mode -- a debugging aid for at-a-glance identification
// while testing, not something a player would ever want on.
const SHOW_RUNE_DESCRIPTIONS_STORAGE_KEY = 'rune-world-game-show-rune-descriptions';

function loadShowRuneDescriptions(): boolean {
  return localStorage.getItem(SHOW_RUNE_DESCRIPTIONS_STORAGE_KEY) === 'true';
}

let showRuneDescriptions: boolean = loadShowRuneDescriptions();

export function getShowRuneDescriptions(): boolean {
  return showRuneDescriptions;
}

export function setShowRuneDescriptions(value: boolean): void {
  showRuneDescriptions = value;
  localStorage.setItem(SHOW_RUNE_DESCRIPTIONS_STORAGE_KEY, String(value));
}

// Developer Tools option: a live-updating debug overlay showing each object's
// (and the player's) real Energy/Charge/Health/Entropy values above their
// sprites in-world -- a developer-only convenience, distinct from Measure
// (the diegetic, cost-bearing way a *player* discovers a hidden quantity).
// Entropy is object-only (no player reserve exists -- nothing ever writes to
// one). Mass and Momentum are deliberately still not offered here: neither
// has any live per-object *state* today (Mass is `mass.tier`, a fixed
// classification, not a mutable quantity; Momentum is transient, existing
// only during an active push, never a resting value to show).
function loadDebugOverlayFlag(key: string): boolean {
  return localStorage.getItem(key) === 'true';
}

const SHOW_ENERGY_DEBUG_KEY = 'rune-world-game-show-energy-debug';
const SHOW_CHARGE_DEBUG_KEY = 'rune-world-game-show-charge-debug';
const SHOW_HEALTH_DEBUG_KEY = 'rune-world-game-show-health-debug';
const SHOW_ENTROPY_DEBUG_KEY = 'rune-world-game-show-entropy-debug';
const SHOW_CONDUCTIVITY_DEBUG_KEY = 'rune-world-game-show-conductivity-debug';
const SHOW_DEV_SIGNPOSTS_KEY = 'rune-world-game-show-dev-signposts';

let showEnergyDebug = loadDebugOverlayFlag(SHOW_ENERGY_DEBUG_KEY);
let showChargeDebug = loadDebugOverlayFlag(SHOW_CHARGE_DEBUG_KEY);
let showHealthDebug = loadDebugOverlayFlag(SHOW_HEALTH_DEBUG_KEY);
let showEntropyDebug = loadDebugOverlayFlag(SHOW_ENTROPY_DEBUG_KEY);
let showConductivityDebug = loadDebugOverlayFlag(SHOW_CONDUCTIVITY_DEBUG_KEY);
// Unlike the three overlay flags above (opt-in, default off), dev signposts
// have always just been present -- this is an opt-*out*, so it defaults to
// shown (true) for anyone who's never touched it, preserving existing
// behavior. Stored as the *hidden* string so an absent key still means "on".
let showDevSignposts = localStorage.getItem(SHOW_DEV_SIGNPOSTS_KEY) !== 'false';
const debugOverlayListeners: Array<() => void> = [];

export function getShowEnergyDebug(): boolean {
  return showEnergyDebug;
}

export function setShowEnergyDebug(value: boolean): void {
  showEnergyDebug = value;
  localStorage.setItem(SHOW_ENERGY_DEBUG_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

export function getShowChargeDebug(): boolean {
  return showChargeDebug;
}

export function setShowChargeDebug(value: boolean): void {
  showChargeDebug = value;
  localStorage.setItem(SHOW_CHARGE_DEBUG_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

export function getShowHealthDebug(): boolean {
  return showHealthDebug;
}

export function setShowHealthDebug(value: boolean): void {
  showHealthDebug = value;
  localStorage.setItem(SHOW_HEALTH_DEBUG_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

export function getShowEntropyDebug(): boolean {
  return showEntropyDebug;
}

export function setShowEntropyDebug(value: boolean): void {
  showEntropyDebug = value;
  localStorage.setItem(SHOW_ENTROPY_DEBUG_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

// Developer Tools option: the one overlay that draws *geometry* rather than
// text. Every object taking part in the Charge system gets an outline saying
// whether it conducts (green), stores without conducting (amber), or is a break
// (red), filled by the potential its own network is sitting at -- which between
// them answer the two questions "why is this circuit dead" actually has, and
// which nothing on screen answered before. A run of cable draws the same picture carrying 8 as carrying
// nothing, and a Charged Font resting *beside* a cable rather than *on* it looks
// exactly like one that is connected; both were real, reported confusions, and
// both are one glance under this.
export function getShowConductivityDebug(): boolean {
  return showConductivityDebug;
}

export function setShowConductivityDebug(value: boolean): void {
  showConductivityDebug = value;
  localStorage.setItem(SHOW_CONDUCTIVITY_DEBUG_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

export function getShowDevSignposts(): boolean {
  return showDevSignposts;
}

// Developer Tools option: hides (and disables Interact on) every
// dev-signpost-* object across every room -- these were always meant as
// internal hint text for building/testing puzzles, never real player-facing
// content, so this is the "give me the game without the dev notes" switch.
// Purely a visibility/interaction toggle (see GameScene's
// refreshDevSignpostVisibility/isDevSignpostHidden) -- the signpost objects
// themselves are never removed from room state, so this can't be confused
// with a real removedObjectIds save mutation the way actually melting a
// barrier would be.
export function setShowDevSignposts(value: boolean): void {
  showDevSignposts = value;
  localStorage.setItem(SHOW_DEV_SIGNPOSTS_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

// GameScene registers this so a checkbox toggle refreshes the in-world labels
// immediately, rather than waiting for the next decay/regen tick or cast.
export function onDebugOverlayChange(listener: () => void): void {
  debugOverlayListeners.push(listener);
}

// Developer Tools option: the "Facing: ..." HUD text (ui.ts's
// #faced-target-label) is a debugging convenience for seeing exactly what
// Interact/Mark/Cast currently resolve to -- the buttons' own enabled state
// and labels already carry that information for a real player, so this
// defaults off rather than being always-on.
const SHOW_FACING_DEBUG_KEY = 'rune-world-game-show-facing-debug';

let showFacingDebug = loadDebugOverlayFlag(SHOW_FACING_DEBUG_KEY);

export function getShowFacingDebug(): boolean {
  return showFacingDebug;
}

export function setShowFacingDebug(value: boolean): void {
  showFacingDebug = value;
  localStorage.setItem(SHOW_FACING_DEBUG_KEY, String(value));
}

// Developer Tools option: labels every tile in the current room with its
// (x,y) grid coordinate, in-world -- lets a developer/tester read a position
// straight off the screen (e.g. "move the candle to (5,3)") instead of
// counting tiles by hand. Uses the same debugOverlayListeners mechanism as
// the Energy/Charge/Health/Entropy overlays so toggling it refreshes the
// room immediately.
const SHOW_GRID_COORDS_KEY = 'rune-world-game-show-grid-coords';

let showGridCoords = loadDebugOverlayFlag(SHOW_GRID_COORDS_KEY);

export function getShowGridCoords(): boolean {
  return showGridCoords;
}

export function setShowGridCoords(value: boolean): void {
  showGridCoords = value;
  localStorage.setItem(SHOW_GRID_COORDS_KEY, String(value));
  debugOverlayListeners.forEach((listener) => listener());
}

// Persisted preference, set from the opening title screen: whether the
// onboarding nudges in tutorialHints.ts (the room0/room1 "press Interact"
// hint and the Equip/Cast tutorial panel) show at all this playthrough.
// Defaults on -- the real first-time experience -- so a returning player who
// wants them off has to actively opt out, not the other way around.
const TUTORIAL_ENABLED_KEY = 'rune-world-game-tutorial-enabled';

function loadTutorialEnabled(): boolean {
  return localStorage.getItem(TUTORIAL_ENABLED_KEY) !== 'false';
}

let tutorialEnabled: boolean = loadTutorialEnabled();

export function getTutorialEnabled(): boolean {
  return tutorialEnabled;
}

export function setTutorialEnabled(value: boolean): void {
  tutorialEnabled = value;
  localStorage.setItem(TUTORIAL_ENABLED_KEY, String(value));
}

// Persisted preference, set from the opening title screen: whether the
// "Developer Tools" button (room jump, debug overlays, revision history, the
// rune-gate-bypass toggle) even appears in the Settings menu. Defaults off,
// unlike devMode above which this is independent of -- a clean build for
// playtesters shouldn't surface dev-only controls unless someone opts in.
const DEV_TOOLS_ENABLED_KEY = 'rune-world-game-dev-tools-enabled';

function loadDevToolsEnabled(): boolean {
  return localStorage.getItem(DEV_TOOLS_ENABLED_KEY) === 'true';
}

let devToolsEnabled: boolean = loadDevToolsEnabled();

export function getDevToolsEnabled(): boolean {
  return devToolsEnabled;
}

export function setDevToolsEnabled(value: boolean): void {
  devToolsEnabled = value;
  localStorage.setItem(DEV_TOOLS_ENABLED_KEY, String(value));
}
